import { combineReducers } from 'redux'
import { createAction, createAsyncAction, createReducer, isActionOf } from 'typesafe-actions'
import { ByManagerData, ByUserQuery, StatisticsData } from '../../../responses/statistics'
import * as O from 'fp-ts/Option'
import { Option } from 'fp-ts/Option'
import { RootEpic } from '../../root'
import { filter, map, mergeMap } from 'rxjs/operators'
import { apiRequests } from '../../../http/config'
import { toastFailedResponse } from '../../../components/controls/Toaster/Toaster'
import {
    HideConfiguration,
    OpenConfiguration,
    SortConfiguration
} from '../../../components/pages/statistics/StatisticsTable/configuration'
import { payloadReducer } from '../../../utils/redux-utils'
import { ID } from '../../../types/UtilityTypes'
import { SortDirection } from '../../../constants/direction'
import { User } from '../../../responses/users'
import {RequestStatus} from '../../../responses/response'

export const byManagerActions = {
    data: createAsyncAction(
        '@STATISTICS_BY_MANAGER/DATA_REQUEST',
        '@STATISTICS_BY_MANAGER/DATA_SUCCESS',
        '@STATISTICS_BY_MANAGER/DATA_FAILURE',
    )<ByUserQuery, StatisticsData<ByManagerData>, void>(),
    setHiddenColumns: createAction('@STATISTICS_BY_MANAGER/SET_HIDDEN_COLUMNS')<HideConfiguration[]>(),
    toggleRow: createAction('@STATISTICS_BY_MANAGER/TOGGLE_ROW')<Record<ID, OpenConfiguration>>(),
    setManagers: createAction('@STATISTICS_BY_MANAGER/SET_MANAGERS')<User[]>(),
    setUsers: createAction('@STATISTICS_BY_MANAGER/SET_USERS')<User[]>(),
    setSort: createAction('@STATISTICS_BY_MANAGER/SET_SORT')<SortConfiguration>(),
    clearFilters: createAction('@STATISTICS_BY_MANAGER/CLEAR_FILTERS')()
}

export interface ByManagerState {
    data: Option<StatisticsData<ByManagerData>>,
    managers: User[],
    users: User[],
    hide: HideConfiguration[],
    open: Record<ID, OpenConfiguration>,
    sort: SortConfiguration,
    state: RequestStatus,
}

export const byManagerReducer = combineReducers<ByManagerState>({
    data: createReducer<Option<StatisticsData<ByManagerData>>>(O.none)
        .handleAction(byManagerActions.data.success, (_, action) => O.of(action.payload)),
    managers: createReducer<User[]>([])
        .handleAction(byManagerActions.setManagers, payloadReducer)
        .handleAction(byManagerActions.clearFilters, () => []),
    users: createReducer<User[]>([])
        .handleAction(byManagerActions.setUsers, payloadReducer)
        .handleAction(byManagerActions.clearFilters, () => []),
    hide: createReducer<HideConfiguration[]>([])
        .handleAction(byManagerActions.setHiddenColumns, payloadReducer),
    open: createReducer<Record<ID, OpenConfiguration>>({})
        .handleAction(byManagerActions.data.success, () => ({}))
        .handleAction(byManagerActions.toggleRow, payloadReducer),
    sort: createReducer<SortConfiguration>({group: '', name: '', direction: SortDirection.ASC})
        .handleAction(byManagerActions.setSort, payloadReducer),
    state: createReducer<RequestStatus>(RequestStatus.INITIAL)
        .handleAction(byManagerActions.data.request, () => RequestStatus.FETCHING)
        .handleAction(byManagerActions.data.success, () => RequestStatus.SUCCESS)
        .handleAction(byManagerActions.data.failure, () => RequestStatus.FAILURE),
})

export const byClientEpic: RootEpic = action$ => action$.pipe(
    filter(isActionOf(byManagerActions.data.request)),
    mergeMap(action => apiRequests.statistics.byManager({query: action.payload})),
    map(({response}) => response.success ?
        byManagerActions.data.success(response) :
        byManagerActions.data.failure(toastFailedResponse(response, 'Не удалось запросить данные диаграммы'))
    ),
)
export const byManagerEpic: RootEpic = action$ => action$.pipe(
    filter(isActionOf(byManagerActions.data.request)),
    mergeMap(action => apiRequests.statistics.byManager({
        query: action.payload
    })),
    map(res => res.response.success?
        byManagerActions.data.success(res.response):
        byManagerActions.data.failure(toastFailedResponse(res.response, 'Загрузка статистики по менеджерам не удалась'))
    )
)